import React, { useEffect, useRef, useState } from 'react';

import { reverse } from 'named-urls';

import { useStore } from 'utils/hooks/store';
import useFileUpload from 'utils/hooks/useFileUpload';
import Logger from 'utils/logger';

import routes from 'core/routes';
import { MAX_IMPORT_DOCUMENT_SIZE, MAX_IMPORT_DOCUMENT_SIZE_LABEL } from 'core/constants';

import { FolderType } from 'stores/Folders/types';

import { useDefaultFolderId } from 'containers/Documents/hooks/useDefaultFolderId';
import {
  importDocx,
  getConvertImageUploadUrl,
  DocxImportError,
  DOCX_MIME_TYPE,
} from 'containers/Tiptap/lib/import-utils';
import { sanitizeImportedContent } from 'containers/Tiptap/lib/import-sanitize';
import { DocumentViewMode } from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';

import {
  stripDocxExtension,
  describeCause,
  COULD_NOT_CREATE_MESSAGE,
  IMPORT_TIMEOUT_MS,
  IMPORT_TIMEOUT_MESSAGE,
  ImportPhase,
} from './ImportDocumentModal.helpers';

export interface UseImportDocument {
  phase: ImportPhase;
  cause: string;
  file: File | null;
  fileError: string;
  dragActive: boolean;
  fileRef: React.RefObject<HTMLInputElement>;
  dropZoneRef: React.RefCallback<HTMLDivElement>;
  destinationFolderId: string;
  setDestinationFolderId: (id: string) => void;
  folderTree: FolderType[];
  destinationPath: string;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (event: React.MouseEvent) => void;
  runImport: () => Promise<void>;
  cancel: () => void;
}

// The .docx import state machine: file selection + validation, destination
// folder, and the create → convert → seed → navigate pipeline (cancel-safe).
export function useImportDocument(onClose: () => void): UseImportDocument {
  const { documentsDetails, folders, notification, routing, session } = useStore();
  const defaultFolderId = useDefaultFolderId();

  const [phase, setPhase] = useState<ImportPhase>('select');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [cause, setCause] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  const [destinationFolderId, setDestinationFolderId] = useState<string>(
    defaultFolderId ?? folders.flatFolders[0]?.uuid ?? ''
  );

  const handleFilePicked = (picked: File | null): void => {
    if (!picked) return;
    const isDocxByName = /\.docx$/i.test(picked.name);
    const isDocxByType = picked.type === DOCX_MIME_TYPE;
    if (!isDocxByName && !isDocxByType) {
      setFile(null);
      setFileError(`‘${picked.name}’ is not a Word file. Choose a Word document.`);
      return;
    }
    if (picked.size > MAX_IMPORT_DOCUMENT_SIZE) {
      setFile(null);
      setFileError(`‘${picked.name}’ is too large. Files must be under ${MAX_IMPORT_DOCUMENT_SIZE_LABEL}.`);
      return;
    }
    setFileError('');
    setFile(picked);
  };

  const { dragActive, fileRef, dropZoneRef } = useFileUpload(handleFilePicked);

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const picked = event.target.files?.[0] ?? null;
    // Reset the input now so re-selecting the same file re-fires `change`.
    event.target.value = '';
    handleFilePicked(picked);
  };

  const onRemoveFile = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setFile(null);
    setFileError('');
  };

  // Cancel can land after the document was created — delete the orphan and
  // refresh the rail (create() already reloaded folders with it visible).
  const discardCancelledImport = (createdId: string): void => {
    void documentsDetails.delete(createdId).then((deleted) => {
      if (deleted) {
        folders.loadFolders();
      } else {
        Logger.error('Failed to discard cancelled import, orphan document left behind', { createdId });
      }
    });
  };

  const runImport = async (): Promise<void> => {
    if (!file || !destinationFolderId) return;
    setPhase('importing');
    setCause('');

    const controller = new AbortController();
    abortRef.current = controller;

    // Nothing in the pipeline is bounded by the browser, so one timer aborts the
    // whole run — otherwise a stalled request leaves the modal spinning forever.
    const timeout = setTimeout(() => {
      controller.abort();
      setCause(IMPORT_TIMEOUT_MESSAGE);
      setPhase('failed');
    }, IMPORT_TIMEOUT_MS);

    let orphanId: string | null = null;
    try {
      const title = stripDocxExtension(file.name);
      const created = await documentsDetails.create(title, destinationFolderId);
      if (typeof created !== 'string') {
        throw new DocxImportError(COULD_NOT_CREATE_MESSAGE);
      }
      orphanId = created;
      if (controller.signal.aborted) return;

      // The cached token expires after 30 minutes and its refresh interval stalls
      // while the tab sleeps, so mint a fresh one rather than risk a 401.
      await session.refreshTiptapTokens(controller.signal);
      if (controller.signal.aborted) return;
      const token = session.user?.tiptapConvertToken || '';
      const imageUploadToken = await documentsDetails.fetchImageUploadToken(created);
      if (controller.signal.aborted) return;

      const { content, numberingFormats, headerFooter } = await importDocx({
        file,
        token,
        signal: controller.signal,
        imageUpload: imageUploadToken
          ? { url: getConvertImageUploadUrl(), token: imageUploadToken, entityId: created }
          : undefined,
      });
      if (controller.signal.aborted) return;

      const seededJson = sanitizeImportedContent(content);
      const viewMode = session.user?.documentsSettingsTabEnabled ? DocumentViewMode.Pages : DocumentViewMode.Pageless;
      documentsDetails.setPendingInitialContent(created, seededJson, numberingFormats, viewMode, headerFooter);
      orphanId = null;

      const destinationName = folders.flatFolders.find((f) => f.uuid === destinationFolderId)?.name;
      const importedMessage = destinationName ? `Document imported into ${destinationName}` : 'Document imported';
      if (imageUploadToken) {
        notification.enqueueSuccessSnackbar(importedMessage);
      } else {
        notification.enqueueWarningSnackbar(`${importedMessage}, but embedded images could not be retained`);
      }
      routing.push(reverse(routes.dashboard.documents.details, { entityId: created }));
      onClose();
    } catch (error) {
      if ((error as { name?: string })?.name === 'AbortError') return;
      Logger.error('Failed to import .docx', error);
      setCause(describeCause(error));
      setPhase('failed');
    } finally {
      clearTimeout(timeout);
      if (abortRef.current === controller) abortRef.current = null;
      if (orphanId) discardCancelledImport(orphanId);
    }
  };

  // Abort the in-flight conversion (Cancel button); also runs on unmount.
  const cancel = (): void => abortRef.current?.abort();
  useEffect(() => cancel, []);

  return {
    phase,
    cause,
    file,
    fileError,
    dragActive,
    fileRef,
    dropZoneRef,
    destinationFolderId,
    setDestinationFolderId,
    folderTree: folders.folders,
    destinationPath: destinationFolderId ? folders.getFolderPath(destinationFolderId) : '',
    onInputChange,
    onRemoveFile,
    runImport,
    cancel,
  };
}
